import { useDisclosure } from '@chakra-ui/react'
import {
  faBoltLightning,
  faExclamationTriangle,
  faUserSecret,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as Sentry from '@sentry/react'
import type React from 'react'
import { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
  useRouteMatch,
} from 'react-router-dom'
import KYCBanner from './components/KYCBanner'
import SupportNoticeBanner from './components/SupportNoticeBanner'
import SuspendedAccountModal from './components/SuspendedAccountModal'
import WelcomeModal from './components/WelcomeModal'
import { INVEST_DEPOSITS_DISABLED, NODE_ENV } from './constants'
import AppAuthorizationChecker from './containers/AppAuthorizationChecker'
import {
  useGetComplianceOnboardingStateQuery,
  useGetUserQuery,
  useStopImpersonationMutation,
} from './redux/services/coreApi'
import { setUser, stopImpersonationSession } from './redux/slices/sessionSlice'
import type { RootState } from './redux/store'
import AppMenu from './screens/App/components/AppMenu'
import AccountScreen from './screens/App/screens/Account/AccountScreen'
import AffiliatePartnerScreen from './screens/App/screens/Admin/AffiliatePartners/AffiliatePartnerScreen'
import AffiliatePartnersScreen from './screens/App/screens/Admin/AffiliatePartners/AffiliatePartnersScreen'
import SystemBankAccountsScreen from './screens/App/screens/Admin/BankAccounts/SystemBankAccountsScreen'
import BookkeepingScreen from './screens/App/screens/Admin/Bookkeeping/BookkeepingScreen'
import ClientScreen from './screens/App/screens/Admin/Clients/ClientScreen'
import ClientsScreen from './screens/App/screens/Admin/Clients/ClientsScreen'
import FinanstilsynetScreen from './screens/App/screens/Admin/Compliance/Finanstilsynet/FinanstilsynetScreen'
import RuleSetsScreen from './screens/App/screens/Admin/Compliance/RuleSets/RuleSetsScreen'
import CurrenciesScreen from './screens/App/screens/Admin/Currencies/CurrenciesScreen'
import CustomerInsightsScreen from './screens/App/screens/Admin/CustomerInsights/CustomerInsightsScreen'
import FeeStructuresScreen from './screens/App/screens/Admin/FeeStructures/FeeStructuresScreen'
import FundScreen from './screens/App/screens/Admin/Fund/FundScreen'
import BookkeeperFundDepositsScreen from './screens/App/screens/Admin/FundDeposits/BookkeeperFundDepositsScreen'
import NetworksScreen from './screens/App/screens/Admin/Networks/NetworksScreen'
import PerformanceScreen from './screens/App/screens/Admin/Performance/PerformanceScreen'
import SystemWalletsScreen from './screens/App/screens/Admin/SystemWallets/SystemWalletsScreen'
import TeamScreen from './screens/App/screens/Admin/Team/TeamScreen'
import BookkeeperTransfersScreen from './screens/App/screens/Admin/Transfers/BookkeeperTransfersScreen'
import TransfersScreen from './screens/App/screens/Admin/Transfers/TransfersScreen'
import VeliMigrationScreen from './screens/App/screens/Admin/VeliMigration/VeliMigrationScreen'
import AdminWealthManagerScreen from './screens/App/screens/Admin/VeliMigration/WealthManagerScreen'
import BankAccountsScreen from './screens/App/screens/BankAccounts/BankAccountsScreen'
import BrokerServiceScreen from './screens/App/screens/Broker Service/BrokerServiceScreen'
import GetStartedScreen from './screens/App/screens/Get Started/GetStartedScreen'
import VeliOnboardingShell from './screens/App/screens/Get Started/VeliOnboardingShell'
import InvestScreen from './screens/App/screens/Invest/InvestScreen'
import InvestorScreen from './screens/App/screens/Investor/InvestorScreen'
import OkxInvestorScreen from './screens/App/screens/Investor/OkxInvestorScreen'
import KycNavigator from './screens/App/screens/Kyc/KycNavigator'
import ThorChainSwap from './screens/App/screens/ThorChainSwap/ThorChainSwap'
import WalletVerifyScreen from './screens/App/screens/Wallets/WalletVerifyScreen'
import WalletsScreen from './screens/App/screens/Wallets/WalletsScreen'
import WealthManagerScreen from './screens/App/screens/WealthManager/WealthManagerScreen'
import MfaEnrollmentScreen from './screens/Mfa/MfaEnrollmentScreen'
import {
  isAdmin,
  isBookkeeper,
  isPartner,
  isWealthManager,
} from './utils/roleUtils'
import { isSupportHolidayNoticeActive } from './utils/supportHolidayNotice'

export const AppNavigator: React.FC = () => {
  const { path } = useRouteMatch()
  const dispatch = useDispatch()

  const welcomeModalHasBeenOpen = useRef(false)
  const welcomeModal = useDisclosure()
  const suspendedAccountModal = useDisclosure()

  const history = useHistory()
  const location = useLocation()

  const sessionUser = useSelector((state: RootState) => state.session.user)
  const isImpersonating = useSelector(
    (state: RootState) => state.session.isImpersonating
  )
  const impersonationExpiresAtUtc = useSelector(
    (state: RootState) => state.session.impersonationExpiresAtUtc
  )
  const [stopImpersonation, { isLoading: isStoppingImpersonation }] =
    useStopImpersonationMutation()
  const adminReturnPath = useSelector(
    (state: RootState) => state.session.adminReturnPath
  )
  const isPartnerUser = isPartner(sessionUser.role)
  const isAdminUser = isAdmin(sessionUser.role)
  const isWealthManagerUser = isWealthManager(sessionUser.role)
  // Veli-migrated clients (ADR-0004) can't use the Investing product until their
  // portfolio is replicated on Penning, so the menu items are greyed out — keep
  // the routes unreachable too so a direct URL doesn't land them on an empty
  // portfolio. Mirrors how INVEST_DEPOSITS_DISABLED guards fund-deposits.
  const isVeliMigrating = sessionUser.isVeliOrigin ?? false
  const user = useGetUserQuery(sessionUser.id)
  const complianceState = useGetComplianceOnboardingStateQuery(undefined, {
    skip: isAdminUser || isPartnerUser || isWealthManagerUser,
  })
  const userHasAcceptedDisclaimers =
    user.data?.hasAcceptedDisclaimers ?? sessionUser.hasAcceptedDisclaimers

  useEffect(() => {
    Sentry.setContext('user', {
      id: sessionUser.id,
      role: sessionUser.role,
      email: sessionUser.email,
    })
  }, [user.data])

  // Keep the session-slice copy of the user in lockstep with the live User
  // query. Screens that gate on onboarding state — Wallets ("Add wallet"),
  // Broker, Invest — read state.session.user, which AppAuthorizationChecker
  // only seeds once at boot. Without this, a status that flips mid-session
  // (e.g. a Sumsub webhook approving KYC, picked up by the Get Started polling
  // query) never reaches those gates until a full page reload. Syncing here, in
  // the always-mounted shell, refreshes them in place whenever anything
  // invalidates or refetches the User tag.
  useEffect(() => {
    if (user.data) {
      dispatch(setUser({ user: user.data }))
    }
  }, [user.data, dispatch])

  useEffect(() => {
    if (!isAdminUser && NODE_ENV !== 'development') {
      if (!userHasAcceptedDisclaimers) {
        const timer = setTimeout(() => {
          welcomeModal.onOpen()
          welcomeModalHasBeenOpen.current = true
        }, 1000)

        return () => clearTimeout(timer)
      }
    }
  }, [welcomeModal.onOpen, isAdminUser, userHasAcceptedDisclaimers])

  useEffect(() => {
    if (userHasAcceptedDisclaimers) {
      welcomeModalHasBeenOpen.current = true
    }
  }, [userHasAcceptedDisclaimers])

  useEffect(() => {
    // Only after the welcomeModal has been closed
    if (
      (welcomeModalHasBeenOpen.current && !welcomeModal.isOpen) ||
      NODE_ENV === 'development'
    ) {
      // Only if we have User Data (async, might come later)
      if (user.data) {
        if (user.data.isSuspended) {
          suspendedAccountModal.onOpen()
        }
      }
    }
  }, [user.data, welcomeModal.isOpen, suspendedAccountModal.onOpen])

  const kycStatus = user.data?.kycDetails?.kycStatus
  const hasAcceptedClientAgreement =
    user.data?.hasAcceptedClientAgreement ?? false
  const riskAssessmentQuestionnaire = user.data?.requiredQuestionnaires?.find(
    q => q.type === 'RiskAssessment'
  )
  const needsRiskAssessment =
    !riskAssessmentQuestionnaire ||
    riskAssessmentQuestionnaire?.status === 'Requested' ||
    riskAssessmentQuestionnaire?.status === 'NotRequested'
  const hasCompletedRiskAssessment =
    riskAssessmentQuestionnaire?.status === 'Completed'
  const isOnboardingComplete =
    kycStatus === 'Approved' &&
    hasAcceptedClientAgreement &&
    (!needsRiskAssessment || hasCompletedRiskAssessment)
  const isOnboardingRoute =
    location.pathname.includes('/get-started') ||
    location.pathname.includes('/kyc')
  const showOnboardingBanner =
    !!user.data && !isOnboardingRoute && !isOnboardingComplete

  // Time-boxed summer-holiday support notice (see SUPPORT_HOLIDAY_NOTICE).
  // Shown to customer-facing users only — internal staff are the ones on
  // holiday, so the banner would just be noise for them.
  const showSupportHolidayNotice =
    isSupportHolidayNoticeActive() && !isAdminUser

  const complianceCopy = complianceState.data?.customerFacingCopy
  // A terminal compliance verdict (a reject/exit) applies even before onboarding is finished — the pre-KYC
  // country gate (ADR-0015) can reject a customer at the very first step. So the compliance notice must show
  // for these states regardless of onboarding completion, not only for a rejected established customer.
  const isTerminalComplianceState =
    complianceState.data?.state === 'Rejected' ||
    complianceState.data?.state === 'Exited'
  const hasAccountFreeze = complianceState.data?.activeRestrictions.some(
    restriction => restriction.kind === 'AccountFreeze'
  )
  const complianceBannerColor =
    isTerminalComplianceState || hasAccountFreeze ? 'red' : 'yellow'

  return (
    <div
      data-testid="app-shell"
      className="h-screen flex flex-col w-full md:flex-row "
    >
      <WelcomeModal
        isOpen={welcomeModal.isOpen}
        onClose={welcomeModal.onClose}
      />
      <SuspendedAccountModal
        isOpen={suspendedAccountModal.isOpen}
        onClose={suspendedAccountModal.onClose}
      />
      {/* The whole platform chrome is stripped for a migrating Veli client
          (ADR-0004): during the interim there's nothing for them to navigate to
          but their setup, so the sidebar — every item of which is a locked
          "coming soon" promise — is hidden entirely. They get a focused landing
          page (VeliOnboardingShell) instead of the full app. */}
      {!isVeliMigrating && <AppMenu />}
      <div className="w-full bg-bg-base transition-all h-screen relative flex flex-col">
        <div className="shrink-0 z-50">
          <SupportNoticeBanner isVisible={showSupportHolidayNotice} />
          <KYCBanner
            isVisible={isImpersonating}
            title="Impersonation mode (read-only)"
            description={`You are viewing this app as ${sessionUser.name || sessionUser.id}. Expires at ${impersonationExpiresAtUtc || 'N/A'} UTC.`}
            buttonText={
              isStoppingImpersonation ? 'Switching back...' : 'Switch back'
            }
            colorCheme="yellow"
            icon={<FontAwesomeIcon icon={faUserSecret} className="text-base" />}
            isLoading={isStoppingImpersonation}
            onClick={async () => {
              const returnTo = adminReturnPath
              try {
                await stopImpersonation().unwrap()
              } catch {
                // Always restore local admin session state even if server-side session already expired.
              } finally {
                dispatch(stopImpersonationSession())
                if (returnTo) {
                  history.push(returnTo)
                }
              }
            }}
          />
          <KYCBanner
            isVisible={
              showOnboardingBanner && !isAdminUser && !isWealthManagerUser
            }
            title="Finish your account setup"
            description="Before you can use the dashboard properly, we need you to complete the Get Started flow."
            buttonText="Continue setup"
            colorCheme="blue"
            icon={
              <FontAwesomeIcon icon={faBoltLightning} className="text-base" />
            }
            onClick={async () => {
              history.push(`${path}/get-started`)
              // setBanner(undefined)
            }}
          />
          <KYCBanner
            isVisible={
              !!complianceCopy &&
              !isAdminUser &&
              !isPartnerUser &&
              !isWealthManagerUser &&
              (isOnboardingComplete || isTerminalComplianceState)
            }
            title={complianceCopy?.title ?? ''}
            description={complianceCopy?.description ?? ''}
            colorCheme={complianceBannerColor}
            icon={
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                className="text-base"
              />
            }
          />
        </div>
        {/* Content region fills the height left under the (fixed) banner area
            and owns the page's vertical space. Each screen's PageWrapper is
            h-full + scrolls inside here, so a tall banner never pushes the page
            bottom out of reach. */}
        <div className="flex-1 min-h-0">
          {isWealthManagerUser ? (
            // A wealth manager gets a scoped area only — none of the investor or
            // admin routes are reachable, so no investor functionality leaks in.
            <Switch>
              <Route path={`${path}/wealth-manager`}>
                <WealthManagerScreen />
              </Route>
              <Route path={`${path}/account`}>
                <AccountScreen />
              </Route>
              <Route path={`${path}/mfa/enroll`}>
                <MfaEnrollmentScreen />
              </Route>
              <Route path={`${path}`}>
                <Redirect to={`${path}/wealth-manager`} />
              </Route>
            </Switch>
          ) : (
            <Switch>
              <Route exact path={`${path}/`}>
                {isAdminUser ? (
                  isBookkeeper(sessionUser.role) ? (
                    <Redirect to={`${path}/admin/transfers`} />
                  ) : (
                    <Redirect to={`${path}/admin/clients`} />
                  )
                ) : isPartnerUser ? (
                  <Redirect to={`${path}/admin/customer-insights`} />
                ) : (
                  <Redirect to={`${path}/get-started`} />
                )}
              </Route>

              <Route path={`${path}/kyc`}>
                <KycNavigator />
              </Route>

              <Route path={`${path}/get-started`}>
                <VeliOnboardingShell>
                  <GetStartedScreen />
                </VeliOnboardingShell>
              </Route>
              {/* During the Veli interim every product surface is locked —
                  the client has no access until cutover (ADR-0004). The menu is
                  hidden, but guard the routes too so a stale tab or direct URL
                  can't land them on a half-built screen. They go back to their
                  setup landing page instead. */}
              <Route path={`${path}/broker-service`}>
                {isVeliMigrating ? (
                  <Redirect to={`${path}/`} />
                ) : (
                  <BrokerServiceScreen />
                )}
              </Route>
              <Route path={`${path}/thorchain-swap`}>
                {isVeliMigrating ? (
                  <Redirect to={`${path}/`} />
                ) : (
                  <ThorChainSwap />
                )}
              </Route>
              <Route path={`${path}/wallets/verify/:walletId`}>
                {isVeliMigrating ? (
                  <Redirect to={`${path}/`} />
                ) : (
                  <WalletVerifyScreen />
                )}
              </Route>
              <Route path={`${path}/wallets`}>
                {isVeliMigrating ? (
                  <Redirect to={`${path}/`} />
                ) : (
                  <WalletsScreen />
                )}
              </Route>
              <Route path={`${path}/bank-accounts`}>
                {isVeliMigrating ? (
                  <Redirect to={`${path}/`} />
                ) : (
                  <BankAccountsScreen />
                )}
              </Route>
              <Route path={`${path}/investor`}>
                {isVeliMigrating ? (
                  <Redirect to={`${path}/`} />
                ) : sessionUser.isOkxIntegrated ? (
                  <OkxInvestorScreen />
                ) : (
                  <InvestorScreen />
                )}
              </Route>
              <Route path={`${path}/fund-deposits`}>
                {INVEST_DEPOSITS_DISABLED || isVeliMigrating ? (
                  <Redirect to={`${path}/`} />
                ) : (
                  <InvestScreen />
                )}
              </Route>
              <Route path={`${path}/account`}>
                <AccountScreen />
              </Route>
              <Route path={`${path}/mfa/enroll`}>
                <MfaEnrollmentScreen />
              </Route>
              <Route path={`${path}/admin/transfers`}>
                {isBookkeeper(sessionUser.role) ? (
                  <BookkeeperTransfersScreen />
                ) : (
                  <TransfersScreen />
                )}
              </Route>
              <Route path={`${path}/admin/fund-deposits`}>
                <BookkeeperFundDepositsScreen />
              </Route>
              <Route path={`${path}/admin/clients/:id`}>
                <ClientScreen />
              </Route>
              <Route path={`${path}/admin/clients`}>
                <ClientsScreen />
              </Route>
              <Route path={`${path}/admin/currencies`}>
                <CurrenciesScreen />
              </Route>
              <Route path={`${path}/admin/Performance`}>
                <PerformanceScreen />
              </Route>
              <Route path={`${path}/admin/fund`}>
                <FundScreen />
              </Route>
              <Route path={`${path}/admin/customer-insights`}>
                <CustomerInsightsScreen />
              </Route>
              <Route path={`${path}/admin/system-wallets`}>
                <SystemWalletsScreen />
              </Route>
              <Route path={`${path}/admin/networks`}>
                <NetworksScreen />
              </Route>
              <Route path={`${path}/admin/bank-accounts`}>
                <SystemBankAccountsScreen />
              </Route>
              <Route path={`${path}/admin/fee-structures`}>
                <FeeStructuresScreen />
              </Route>
              <Route path={`${path}/admin/affiliate-partners/:id`}>
                <AffiliatePartnerScreen />
              </Route>
              <Route path={`${path}/admin/affiliate-partners`}>
                <AffiliatePartnersScreen />
              </Route>
              <Route path={`${path}/admin/team`}>
                <TeamScreen />
              </Route>
              <Route path={`${path}/admin/veli-migration/:wealthManagerId`}>
                <AdminWealthManagerScreen />
              </Route>
              <Route path={`${path}/admin/veli-migration`}>
                <VeliMigrationScreen />
              </Route>
              <Route path={`${path}/admin/bookkeeping`}>
                <BookkeepingScreen />
              </Route>
              <Route path={`${path}/admin/compliance/finanstilsynet`}>
                <FinanstilsynetScreen />
              </Route>
              <Route path={`${path}/admin/compliance/rule-sets`}>
                <RuleSetsScreen />
              </Route>
            </Switch>
          )}
        </div>
      </div>
    </div>
  )
}

// TODO: This could probably be done cleaner
const WrappedWithAuthorization: React.FC = () => {
  return (
    <AppAuthorizationChecker>
      <AppNavigator />
    </AppAuthorizationChecker>
  )
}

export default WrappedWithAuthorization
